const MAX_IMAGE_DIMENSION = 1600;
const IMAGE_UPLOAD_QUALITY = 0.82;

// Files larger than this are rejected before any decoding attempt.
// Loading a 30MB+ raw image into a canvas can exhaust memory on mobile devices,
// causing long hangs or browser crashes. Moderately oversized files (5–25MB)
// are still accepted because compression typically brings them under the 5MB limit.
const MAX_RAW_FILE_BYTES = 25 * 1024 * 1024; // 25MB

// Detect canvas WebP encoding support once per page load.
// WebP is preferred: better compression than JPEG and preserves alpha transparency.
// Falls back to JPEG on browsers that don't support WebP canvas encoding (Safari < 14).
const getOutputMime = (() => {
  let cached = null;
  return () => {
    if (cached === null) {
      const c = document.createElement("canvas");
      c.width = 1;
      c.height = 1;
      cached = c.toDataURL("image/webp").startsWith("data:image/webp")
        ? "image/webp"
        : "image/jpeg";
    }
    return cached;
  };
})();

// Used when canvas compression is not applicable: non-image files or files with
// an unknown/missing type (which can occur for images from some sources).
const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () => {
      console.warn(
        `[imageCompression] FileReader failed for ${file.name} (type=${file.type}, size=${file.size}B). File may not be downloaded from cloud storage.`,
      );
      reject(
        new Error(
          `Could not read ${file.name}. This photo may not be fully downloaded to your device. ` +
            `On Android, open Google Photos and download the photo first. ` +
            `On iPhone, open the photo in the Photos app to download it from iCloud, then try again.`,
        ),
      );
    };
    reader.readAsDataURL(file);
  });

// Reads the file as a compact ArrayBuffer (no base64 expansion).
// ERR_UPLOAD_FILE_CHANGED safe: once FileReader has the bytes, the original
// file on disk can be modified without affecting our in-memory copy.
const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () => {
      console.warn(
        `[imageCompression] FileReader failed for ${file.name} (type=${file.type}, size=${file.size}B). File may not be downloaded from cloud storage.`,
      );
      reject(
        new Error(
          `Could not read ${file.name}. This photo may not be fully downloaded to your device. ` +
            `On Android, open Google Photos and download the photo first. ` +
            `On iPhone, open the photo in the Photos app to download it from iCloud, then try again.`,
        ),
      );
    };
    reader.readAsArrayBuffer(file);
  });

// Converts an ArrayBuffer to a base64 data URL.
// Only called in fallback paths to avoid the ~33% base64 heap overhead on the
// normal (successfully-compressed) path.
const arrayBufferToDataUrl = (buffer, mimeType) => {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 32768; // 0x8000 — safe limit for Function.apply argument count across all engines
  const parts = [];
  for (let i = 0; i < bytes.length; i += CHUNK) {
    parts.push(String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK)));
  }
  return `data:${mimeType};base64,${btoa(parts.join(""))}`;
};

// Loads an image from an object URL pointing to a Blob we own.
// Revokes the URL on both success and error so the Blob can be released.
const loadImageFromObjectUrl = (objectUrl, fileName) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to decode image: ${fileName}`));
    };
    img.src = objectUrl;
  });

/**
 * Compresses an image file for upload by resizing to at most MAX_IMAGE_DIMENSION
 * on the longest side and re-encoding as WebP (or JPEG as fallback).
 *
 * Reads the file atomically as an ArrayBuffer first to avoid both
 * ERR_UPLOAD_FILE_CHANGED (Android Media Scanner) and the ~33% base64 heap
 * overhead of reading as a data URL up front. The Blob object URL used for
 * canvas decoding points to our in-memory copy, not the original file.
 * The base64 data URL is only built lazily in fallback paths.
 *
 * If the browser cannot decode the in-memory image for canvas compression,
 * falls back to sending the raw data URL so the server can attempt to handle
 * it with sharp.
 *
 * @param {File} file
 * @returns {Promise<string>} data URL of the compressed (or original) image
 */
export const compressImageForUpload = async (file) => {
  if (!file.type || !file.type.startsWith("image/")) {
    return readFileAsDataUrl(file);
  }

  if (file.size > MAX_RAW_FILE_BYTES) {
    throw new Error(
      `${file.name} is too large to process (${(file.size / (1024 * 1024)).toFixed(0)}MB). Please use a photo under 25MB.`,
    );
  }

  // Read entire file into an ArrayBuffer atomically. This is compact (~1 byte/byte,
  // vs ~2 bytes/char for a data URL string) and immune to ERR_UPLOAD_FILE_CHANGED
  // because the resulting Blob is backed by our memory, not the file on disk.
  const arrayBuffer = await readFileAsArrayBuffer(file);

  const blob = new Blob([arrayBuffer], { type: file.type });
  const objectUrl = URL.createObjectURL(blob);

  let img;
  try {
    img = await loadImageFromObjectUrl(objectUrl, file.name);
    // objectUrl is revoked inside loadImageFromObjectUrl on resolve/reject
  } catch (decodeErr) {
    // Browser could not decode the image (unsupported format, memory pressure, etc.).
    // Send the raw data URL and let the server validate it with sharp — this
    // surfaces a clearer error (e.g. "Unsupported image type") if needed.
    console.warn(
      `[imageCompression] Could not decode ${file.name} (type=${file.type}, size=${file.size}B) in browser — sending raw file: ${decodeErr.message}`,
    );
    return arrayBufferToDataUrl(arrayBuffer, file.type);
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return arrayBufferToDataUrl(arrayBuffer, file.type);
  }

  const originalWidth = img.naturalWidth || img.width;
  const originalHeight = img.naturalHeight || img.height;

  if (!originalWidth || !originalHeight) {
    return arrayBufferToDataUrl(arrayBuffer, file.type);
  }

  const maxSide = Math.max(originalWidth, originalHeight);
  const scale =
    maxSide > MAX_IMAGE_DIMENSION ? MAX_IMAGE_DIMENSION / maxSide : 1;
  const targetWidth = Math.max(1, Math.round(originalWidth * scale));
  const targetHeight = Math.max(1, Math.round(originalHeight * scale));

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const outputMime = getOutputMime();
  // JPEG has no alpha channel; prefill white so transparent areas don't render black
  if (outputMime === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  const compressedDataUrl = canvas.toDataURL(outputMime, IMAGE_UPLOAD_QUALITY);

  canvas.width = 1;
  canvas.height = 1;

  if (!compressedDataUrl || compressedDataUrl.length === 0) {
    return arrayBufferToDataUrl(arrayBuffer, file.type);
  }

  // If compression makes payload larger, keep original encoding.
  // Compare decoded bytes of the compressed output against the original file size.
  const compressedBase64 = compressedDataUrl.slice(compressedDataUrl.indexOf(",") + 1);
  const compressedPadding = (compressedBase64.match(/={1,2}$/) || [""])[0].length;
  const compressedBytes = Math.floor((compressedBase64.length * 3) / 4) - compressedPadding;
  if (compressedBytes >= file.size) {
    return arrayBufferToDataUrl(arrayBuffer, file.type);
  }

  return compressedDataUrl;
};
